import {
  InvoiceAmountReadable,
  InvoiceReadable,
  invoicesDownloadRetrieveOptions,
  invoicesResendEmailRetrieveOptions,
  invoicesRetrieveOptions,
  invoicesRetrieveQueryKey,
  ReturnShortContactReadable
} from '@crm/api'
import { DescriptionLabel } from '@crm/components'
import {
  ChangeStatusLabel,
  EditInvoiceModal
} from '@crm/features/billing/components'
import { BillingMainTemplate } from '@crm/features/billing/templates'
import { useLocales } from '@shared/locales'
import {
  DATE_TIME_FORMAT,
  downloadFile,
  format,
  showApiErrors
} from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Flex,
  notification,
  Row,
  Typography
} from 'antd'

import { useState } from 'react'
import { useParams } from 'react-router-dom'

import type { DescriptionsProps } from 'antd'

const { Title, Text } = Typography

export const InvoiceDetailsPage = () => {
  const { t } = useLocales()
  const { id } = useParams()

  const [editInvoice, setEditInvoice] = useState<InvoiceReadable>()

  const { data, isLoading } = useQuery({
    ...invoicesRetrieveOptions({ path: { id: Number(id) } }),
    enabled: Boolean(id)
  })

  const amountDescriptionLabels = {
    title: t(`billing.title`),
    quantity: t(`billing.quantity`),
    amount: t(`billing.amount`),
    tax: t(`billing.tax`),
    total: t(`billing.total`)
  }

  const descriptionItems: DescriptionsProps['items'] = Object.entries(
    amountDescriptionLabels
  )?.map(([title, label]) => ({
    key: title,
    label,
    children: data?.amounts?.[0]?.[title as keyof InvoiceAmountReadable]
  }))

  const { refetch: resendEmail, isLoading: isLoadingResend } = useQuery({
    ...invoicesResendEmailRetrieveOptions({
      path: { id: Number(id) }
    }),
    enabled: false
  })

  const { refetch: download, isLoading: isLoadingDownload } = useQuery({
    ...invoicesDownloadRetrieveOptions({
      path: { id: Number(id) }
    }),
    enabled: false
  })

  const onResendEmail = async () => {
    try {
      const response = await resendEmail()

      if (response?.status === 'success') {
        notification.success({
          message: t('success.success'),
          description: t('success.success_resend_email')
        })
      }
    } catch (error) {
      showApiErrors(error)
    }
  }

  const onDownloadPDF = async () => {
    try {
      const response = await download()
      if (response?.data) {
        downloadFile(response.data, `${data?.invoice_number}.pdf`)
        notification.success({
          message: t('success.success'),
          description: t('success.export_started')
        })
      }
    } catch (error) {
      showApiErrors(error)
    }
  }

  const onEditInvoiceModalClose = () => setEditInvoice(undefined)

  return (
    <>
      <EditInvoiceModal
        open={!!editInvoice}
        invoice={editInvoice}
        onClose={onEditInvoiceModalClose}
      />

      <BillingMainTemplate
        title={data?.invoice_number ?? ''}
        rightComponents={
          <Flex gap={10}>
            <Button
              type="text"
              onClick={onDownloadPDF}
              loading={isLoadingDownload}
            >
              {t('billing.download')}
            </Button>

            <Button onClick={onResendEmail} loading={isLoadingResend}>
              {t('billing.resend')}
            </Button>
          </Flex>
        }
      >
        <Row gutter={20}>
          <Col span={12}>
            <Card
              loading={isLoading}
              title={
                <Flex justify="space-between">
                  <Title level={5} className="mb-0">
                    {t('billing.project_details')}
                  </Title>

                  {data && (
                    <ChangeStatusLabel
                      invoice={data}
                      invoicesQueryKey={[
                        invoicesRetrieveQueryKey({ path: { id: Number(id) } })
                      ]}
                    />
                  )}
                </Flex>
              }
            >
              <Flex vertical gap={10}>
                <Flex vertical gap={6}>
                  <Row>
                    <Col span={12}>
                      <DescriptionLabel
                        vertical
                        label={t('billing.product_name')}
                        info={data?.product?.name}
                      />
                    </Col>

                    <Col span={12}>
                      <DescriptionLabel
                        vertical
                        label={t('billing.title')}
                        info={data?.title}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col span={12}>
                      <DescriptionLabel
                        vertical
                        label={t('billing.currency')}
                        info={data?.currency?.name}
                      />
                    </Col>

                    <Col span={12}>
                      <DescriptionLabel
                        vertical
                        label={t('billing.received_currency')}
                        info={data?.received_currency?.name}
                      />
                    </Col>
                  </Row>
                </Flex>

                <Flex vertical>
                  <Text type="secondary">{t('billing.amount')}</Text>
                  <Descriptions
                    layout="vertical"
                    bordered
                    items={descriptionItems}
                    column={5}
                  />
                </Flex>

                <Row>
                  <Col span={12}>
                    <DescriptionLabel
                      vertical
                      label={t('billing.received_amount')}
                      info={data?.received_amount ?? ''}
                    />
                  </Col>

                  <Col span={12}>
                    <DescriptionLabel
                      vertical
                      label={t('billing.pay_date')}
                      info={data?.pay_date ?? ''}
                    />
                  </Col>
                </Row>

                <Divider />

                <Flex vertical gap={6}>
                  <Flex justify="space-between" align="center">
                    <Title level={5} className="m-0">
                      {t('billing.contacts')}
                    </Title>

                    <Button type="text" onClick={() => setEditInvoice(data)}>
                      {t('buttons.edit')}
                    </Button>
                  </Flex>

                  <Row>
                    <Col span={12}>
                      <DescriptionLabel
                        vertical
                        label={t('billing.contractor')}
                        info={data?.lead?.name ?? ''}
                      />
                    </Col>

                    <Col span={12}>
                      <DescriptionLabel
                        vertical
                        label={t('billing.contacts')}
                        info={(data?.contacts || [])
                          .map((contact: ReturnShortContactReadable) =>
                            [contact.first_name, contact.last_name].join(' ')
                          )
                          .join(', ')}
                      />
                    </Col>
                  </Row>

                  <DescriptionLabel
                    vertical
                    label={t('billing.additional_emails')}
                    info={(data?.additional_emails || []).join(', ')}
                  />
                </Flex>

                <Divider />

                <Flex vertical gap={6}>
                  <Title level={5}>{t('billing.deadline')}</Title>

                  <Row>
                    <Col span={12}>
                      <DescriptionLabel
                        vertical
                        label={t('billing.valid_for')}
                        info={format(data?.due_date)}
                      />
                    </Col>

                    <Col span={12}>
                      <DescriptionLabel
                        vertical
                        label={t('billing.remind_in')}
                        info={data?.remind_periodicity}
                      />
                    </Col>
                  </Row>

                  <DescriptionLabel
                    vertical
                    label={t('billing.created_at')}
                    info={format(data?.created_at, DATE_TIME_FORMAT)}
                  />
                </Flex>
              </Flex>
            </Card>
          </Col>

          <Col span={12}>
            <Card title={t('billing.email_message')}>
              <div
                dangerouslySetInnerHTML={{ __html: data?.email_message || '' }}
              />
            </Card>
          </Col>
        </Row>
      </BillingMainTemplate>
    </>
  )
}
